import { useState } from "react";
import { motion } from "motion/react";
import { Filter } from "lucide-react";
import { EnhancedProjectCard } from "../components/enhanced-project-card";
import { projects } from "../data/projects";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export function Projects() {
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [goalFilter, setGoalFilter] = useState<string>("all");

  const locations = Array.from(
    new Set(projects.map((p) => p.location.split(",")[0]))
  );

  const filteredProjects = projects.filter((project) => {
    const matchesLocation =
      locationFilter === "all" || project.location.includes(locationFilter);
    const matchesStatus =
      statusFilter === "all" || project.status === statusFilter;
    const matchesGoal =
      goalFilter === "all" ||
      project.unwtoGoals.includes(parseInt(goalFilter));

    return matchesLocation && matchesStatus && matchesGoal;
  });

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl mb-6 font-bold">
              Choose Your Impact
            </h1>
            <p className="text-xl md:text-2xl opacity-95 leading-relaxed">
              Every project is verified, transparent, and ready to create
              lasting change. Your contribution funds 100% of the work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-border sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex items-center gap-2 text-foreground">
              <Filter size={20} />
              <span>Filter Projects:</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={goalFilter} onValueChange={setGoalFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="UNWTO Goal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Goals</SelectItem>
                  <SelectItem value="8">SDG 8: Economic Growth</SelectItem>
                  <SelectItem value="10">SDG 10: Reduce Inequality</SelectItem>
                  <SelectItem value="12">
                    SDG 12: Responsible Consumption
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground">
              {filteredProjects.length} project
              {filteredProjects.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProjects.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="flex w-full max-w-[520px] justify-center"
                >
                  <EnhancedProjectCard {...project} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-2xl text-muted-foreground">
                No projects found matching your filters.
              </p>
              <p className="text-lg text-muted-foreground mt-2">
                Try adjusting your selection.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
